import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import ReactSelect, {
  components,
  ControlProps,
  GroupBase,
  OptionProps,
  SelectInstance,
  SingleValue,
  StylesConfig,
} from "react-select";

import { ReactComponent as ChevronDown } from "assets/images/iconsV2/chevron-down.svg";
import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check.svg";
import { ReactComponent as ParcelBox } from "assets/images/icons/ParcelBox.svg";
import { ReactComponent as RateCardIcon } from "assets/images/icons/RateCard.svg";
import { classNames } from "helpers/commonHelpers";

import { ProviderOption, SelectedParcelAccount } from "@RequestFormV2/sections/Provider/types";
import { isProviderEntrySelected } from "@RequestFormV2/sections/Provider/utils/getProviderSelectionKey";

import "./ProviderCard.scss";

type SelectOption = { label: string; value: string };

type ProviderCardProps = {
  provider: ProviderOption;
  selected: boolean;
  selectedParcelAccounts: SelectedParcelAccount[];
  allProvidersRequired?: boolean;
  isAllProvidersMandatory?: boolean;
  onToggle: (provider: ProviderOption, hasParcelAccounts?: boolean) => void;
  onParcelAccountChange: (provider: ProviderOption, accountId: string | null) => void;
  showRateCardBadge?: boolean;
};

type MenuLayout = { width: number; left: number };

function createParcelSelectStyles(menuLayout: MenuLayout | null): StylesConfig<SelectOption> {
  return {
    container: (base) => ({
      ...base,
      flex: "1 0 0",
      minWidth: 0,
    }),
    control: (base) => ({
      ...base,
      minHeight: "unset",
      height: "100%",
      border: "none",
      boxShadow: "none",
      background: "transparent",
      cursor: "pointer",
      padding: 0,
    }),
    valueContainer: (base) => ({
      ...base,
      position: "absolute",
      width: 1,
      height: 1,
      padding: 0,
      margin: 0,
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: 0,
    }),
    input: (base) => ({
      ...base,
      opacity: 0,
      minWidth: 1,
    }),
    indicatorsContainer: () => ({
      display: "none",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
      ...(menuLayout
        ? {
            width: menuLayout.width,
            minWidth: menuLayout.width,
            left: menuLayout.left,
          }
        : {}),
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      width: "100%",
      minWidth: "100%",
    }),
  };
}

function ParcelSelectOption(props: OptionProps<SelectOption, false, GroupBase<SelectOption>>) {
  const { innerProps, innerRef, isSelected, label } = props;
  const { className: optionClassName, ...optionInnerProps } = innerProps;

  return (
    <div
      ref={innerRef}
      {...optionInnerProps}
      className={classNames(optionClassName, "provider-card__menu-option", {
        "provider-card__menu-option--selected": isSelected,
      })}
    >
      <span className="provider-card__menu-label">{label}</span>
      {isSelected && (
        <span className="provider-card__menu-check" aria-hidden>
          <CheckIcon />
        </span>
      )}
    </div>
  );
}

function ProviderCardAvatar({
  provider,
  logoError,
  onLogoError,
}: {
  provider: ProviderOption;
  logoError: boolean;
  onLogoError: () => void;
}) {
  const logoUrl = typeof provider.company_logo === "string" ? provider.company_logo : "";

  return (
    <div className="provider-card__avatar">
      {logoUrl && !logoError ? (
        <img src={logoUrl} alt={provider.company_name} className="provider-card__avatar-img" onError={onLogoError} />
      ) : (
        <div className="provider-card__avatar-fallback">
          <ParcelBox />
        </div>
      )}
      <div className="provider-card__avatar-border" />
    </div>
  );
}

function ProviderCardCheckbox({ selected }: { selected: boolean }) {
  return (
    <span className={classNames("provider-card__checkbox", { "provider-card__checkbox--checked": selected })}>
      {selected && (
        <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="1.6667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

function createParcelSelectControl(
  provider: ProviderOption,
  logoError: boolean,
  onLogoError: () => void,
  subtextPlaceholder: string,
  onOpenMenu: () => void,
  showRateCardBadge: boolean,
) {
  return function ParcelSelectControl(props: ControlProps<SelectOption, false, GroupBase<SelectOption>>) {
    const selectedOption = props.getValue()[0];
    const hasAccountSelected = Boolean(selectedOption);
    const { onMouseDown: controlOnMouseDown, ...restInnerProps } = props.innerProps;

    return (
      <components.Control
        {...props}
        innerProps={{
          ...restInnerProps,
          onMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => {
            const wasOpen = props.selectProps.menuIsOpen;
            controlOnMouseDown?.(event);
            if (!wasOpen) {
              onOpenMenu();
            }
          },
        }}
      >
        <span className="provider-card__chevron">
          <ChevronDown />
        </span>

        <div className="provider-card__info">
          <ProviderCardAvatar provider={provider} logoError={logoError} onLogoError={onLogoError} />

          {showRateCardBadge && provider.rate_card_available && (
            <span className="provider-card__rate-card">
              <RateCardIcon />
            </span>
          )}

          <div
            className={classNames("provider-card__text", {
              "provider-card__text--account-selected": hasAccountSelected,
            })}
          >
            {hasAccountSelected ? (
              <p className="provider-card__name">{selectedOption.label}</p>
            ) : (
              <>
                <p className="provider-card__name">{provider.company_name}</p>
                <p className="provider-card__subtext">{subtextPlaceholder}</p>
              </>
            )}
          </div>
        </div>
      </components.Control>
    );
  };
}

export default function ProviderCard({
  provider,
  selected,
  selectedParcelAccounts,
  allProvidersRequired = false,
  isAllProvidersMandatory = false,
  onToggle,
  onParcelAccountChange,
  showRateCardBadge = true,
}: ProviderCardProps) {
  const intl = useIntl();
  const [logoError, setLogoError] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [isKeyboardMenuNav, setIsKeyboardMenuNav] = useState(false);
  const [menuLayout, setMenuLayout] = useState<MenuLayout | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const parcelSelectRef = useRef<SelectInstance<SelectOption, false, GroupBase<SelectOption>>>(null);
  const isMenuOpenRef = useRef(false);

  const syncMenuLayout = useCallback(() => {
    if (!cardRef.current) return;

    const { width, left } = cardRef.current.getBoundingClientRect();
    setMenuLayout({ width, left });
  }, []);

  const openParcelMenu = useCallback(() => {
    syncMenuLayout();
    setIsKeyboardMenuNav(false);
    setMenuIsOpen(true);
    isMenuOpenRef.current = true;
  }, [syncMenuLayout]);

  const closeParcelMenu = useCallback(() => {
    setMenuIsOpen(false);
    setIsKeyboardMenuNav(false);
    isMenuOpenRef.current = false;
    parcelSelectRef.current?.blur();
  }, []);

  const handleParcelMenuKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (["ArrowDown", "ArrowUp", "Home", "End", "Enter", " "].includes(event.key)) {
      setIsKeyboardMenuNav(true);
    }
  }, []);

  useEffect(() => {
    if (!menuIsOpen) return undefined;

    syncMenuLayout();
    window.addEventListener("resize", syncMenuLayout);
    window.addEventListener("scroll", syncMenuLayout, true);

    return () => {
      window.removeEventListener("resize", syncMenuLayout);
      window.removeEventListener("scroll", syncMenuLayout, true);
    };
  }, [menuIsOpen, syncMenuLayout]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isMenuOpenRef.current) return;

      const target = event.target as Node;
      const inCard = Boolean(cardRef.current?.contains(target));
      const inMenu = Boolean((target as Element).closest?.(".cb-select__menu, .cb-select__menu-portal"));

      if (inCard || inMenu) return;

      closeParcelMenu();
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeParcelMenu]);

  const hasParcelAccounts = provider.parcel_accounts.length > 0;
  const selectedAccountId = selectedParcelAccounts.find((item) =>
    isProviderEntrySelected(provider, [item.provider_id]),
  )?.parcel_account_id;
  const selectValue = selectedAccountId != null ? String(selectedAccountId) : null;
  const selectOptions = provider.parcel_accounts.map(({ label, value }) => ({ label, value }));

  const cardClassName = classNames("provider-card", {
    "provider-card--selected": selected,
    "provider-card--parcel": hasParcelAccounts,
    "provider-card--mandatory": isAllProvidersMandatory,
  });

  const subtextPlaceholder = intl.formatMessage({
    id: "request-form.provider.providerSelection.selectParcelAccount",
    defaultMessage: "Select account",
  });

  const toggleLabel = intl.formatMessage({
    id: "request-form.provider.providerSelection.toggleProvider",
    defaultMessage: "Toggle provider selection",
  });

  const parcelSelectComponents = useMemo(
    () => ({
      Control: createParcelSelectControl(
        provider,
        logoError,
        () => setLogoError(true),
        subtextPlaceholder,
        openParcelMenu,
        showRateCardBadge,
      ),
      Option: ParcelSelectOption,
    }),
    [provider, logoError, subtextPlaceholder, openParcelMenu, showRateCardBadge],
  );

  const parcelSelectStyles = useMemo(() => createParcelSelectStyles(menuLayout), [menuLayout]);

  if (hasParcelAccounts) {
    const selectedOption = selectOptions.find((o) => o.value === selectValue) ?? null;

    return (
      <div ref={cardRef} className={cardClassName}>
        <div className="provider-card__select-area selectV2 provider-card__parcel-select">
          <ReactSelect<SelectOption, false, GroupBase<SelectOption>>
            ref={parcelSelectRef}
            classNamePrefix="cb-select"
            classNames={{
              menuPortal: () =>
                classNames("provider-card__menu-portal", {
                  "provider-card__menu-portal--keyboard-nav": isKeyboardMenuNav,
                }),
              menu: () => "provider-card__menu",
            }}
            onKeyDown={handleParcelMenuKeyDown}
            options={selectOptions}
            value={selectedOption}
            onChange={(option: SingleValue<SelectOption>) => {
              onParcelAccountChange(provider, option?.value ?? null);
              closeParcelMenu();
            }}
            isSearchable={false}
            isClearable={!allProvidersRequired}
            controlShouldRenderValue={false}
            menuIsOpen={menuIsOpen}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
            onMenuOpen={openParcelMenu}
            onMenuClose={() => {
              setMenuIsOpen(false);
              setIsKeyboardMenuNav(false);
              isMenuOpenRef.current = false;
            }}
            styles={parcelSelectStyles}
            components={parcelSelectComponents}
          />
        </div>

        <button
          type="button"
          className="provider-card__checkbox-button"
          aria-label={toggleLabel}
          disabled={allProvidersRequired && selected}
          onClick={(e) => {
            e.stopPropagation();
            if (selected) {
              onToggle(provider, true);
              closeParcelMenu();
            }
          }}
        >
          <ProviderCardCheckbox selected={selected} />
        </button>
      </div>
    );
  }

  const mainContent = (
    <div className="provider-card__info">
      <ProviderCardAvatar provider={provider} logoError={logoError} onLogoError={() => setLogoError(true)} />

      {showRateCardBadge && provider.rate_card_available && (
        <span className="provider-card__rate-card">
          <RateCardIcon />
        </span>
      )}

      <div className="provider-card__text">
        <p className="provider-card__name">{provider.company_name}</p>
      </div>
    </div>
  );

  return (
    <div className={cardClassName}>
      <button
        type="button"
        className="provider-card__main"
        disabled={allProvidersRequired && selected}
        onClick={() => onToggle(provider)}
      >
        {mainContent}
      </button>
      <button
        type="button"
        className="provider-card__checkbox-button"
        aria-label={toggleLabel}
        disabled={allProvidersRequired && selected}
        onClick={(e) => {
          e.stopPropagation();
          onToggle(provider);
        }}
      >
        <ProviderCardCheckbox selected={selected} />
      </button>
    </div>
  );
}
